import { SYSTEM_WORKFLOW_LIMITS, WORKFLOW_LIMITS, type AddStepPresetId, type WorkflowStepType, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, DialogTrigger, Icon, Popover, PopoverContent, PopoverTrigger, toast } from "@sagot/ui";
import { useState } from "react";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { PathDef } from "../types";
import type { AddStepEntry } from "./add-step/add-step-catalog";
import type { SectionId } from "./add-step/add-step-model";
import { AddStepPanel } from "./add-step/add-step-panel";
import { useHistory } from "./history-context";
import { useDefaultWorkflow } from "./system/default-context";
import { isTerminalStep } from "./add-step/step-exits";
import { useNarrowScreen } from "./add-step/use-narrow-screen";

const B = "engage.workflows.builder";

interface AddStepMenuProps {
  trigger: WorkflowTrigger;
  pathId: string;
  path: PathDef;
  /** The workflow cannot be edited. */
  disabled: boolean;
  onAdd: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
  /** Swaps the last step of the path for another (offered when nothing can follow that step). */
  onReplace: (type: WorkflowStepType, preset?: AddStepPresetId) => void;
}

/** Bottom sheet below 480 px: full width, flush with the bottom edge, rounded on top only. */
const SHEET_CLASS = "-mx-4 -mb-4 w-screen max-w-none self-end rounded-b-none pb-[env(safe-area-inset-bottom)]";
/** The popover's own `w-72 p-3` are overridden with `!`: the classes are joined, not merged, so the later rule must win by importance. */
const POPOVER_CLASS = "flex max-h-[min(70vh,var(--radix-popover-content-available-height))] w-[360px]! max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0!";

/**
 * "Add step": a searchable menu of the steps for the end of one path, sections of "Suggested next steps", "Add content to
 * path", "Actions" and "Flow", or "Replace with" when nothing can follow the path's last step. The trigger and plan gates
 * are the old menu's; locked steps stay in the list. See `add-step/add-step-model.ts`.
 */
export function AddStepMenu({ trigger, pathId, path, disabled, onAdd, onReplace }: AddStepMenuProps) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const history = useHistory();
  const narrow = useNarrowScreen();
  const [open, setOpen] = useState(false);
  // Popover or bottom sheet is decided when the menu opens: resizing the window with it open (a screenshot tool, a keyboard
  // sliding up) must not swap one for the other and throw away what was typed.
  const [openedNarrow, setOpenedNarrow] = useState(false);
  const asSheet = open ? openedNarrow : narrow;
  const onOpenChange = (next: boolean) => {
    if (next) setOpenedNarrow(narrow);
    setOpen(next);
  };
  const last = path.steps.at(-1);
  // A default workflow stays basic: fewer steps in a path than a workflow of the workspace's own.
  const isDefault = useDefaultWorkflow() !== null;
  const full = path.steps.length >= (isDefault ? SYSTEM_WORKFLOW_LIMITS.stepsPerPath : WORKFLOW_LIMITS.stepsPerPath);
  const blocked = disabled || (full && !(last && isTerminalStep(last)));

  const choose = (entry: AddStepEntry, section: SectionId) => {
    setOpen(false);
    if (section !== "replace" || !last) {
      onAdd(entry.type, entry.preset);
      return;
    }
    const before = history?.latest();
    onReplace(entry.type, entry.preset);
    const message = t("engage.workflows.addStep.replaced", { from: t(`engage.workflows.steps.${last.type}`), to: entry.label });
    const replaced = history?.latest();
    if (!history || replaced === undefined || replaced === before) {
      toast.success(message);
      return;
    }
    // Undo takes the swap back, but only while it is still the last edit: a later one is not the toast's to undo.
    toast.success(message, { action: { label: t("engage.workflows.extras.history.undo"), onClick: () => { history.undoIfCurrent(replaced); } } });
  };
  const button = (
    <Button type="button" variant="secondary" size="sm" disabled={blocked}>
      <Icon name="plus" />
      {t(`${B}.addStep`)}
    </Button>
  );
  const panel = (className: string) => <AddStepPanel trigger={trigger} plan={plan} pathId={pathId} path={path} className={className} onChoose={choose} />;
  if (asSheet) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{button}</DialogTrigger>
        <DialogContent title={t(`${B}.addStep`)} closeLabel={t("common.close")} className={SHEET_CLASS}>
          {panel("max-h-[60vh]")}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent aria-label={t(`${B}.addStep`)} collisionPadding={16} className={POPOVER_CLASS}>
        {panel("min-h-0 flex-1")}
      </PopoverContent>
    </Popover>
  );
}
